import { Box, Paper, styled } from '@mui/material';

export const Form = styled('form', {
  shouldForwardProp: (prop) => prop !== 'isNote',
})<{ isNote: boolean }>(({ theme, isNote }) => ({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-between',
  flex: 1,
  overflow: 'auto',
  backgroundColor: isNote ? theme.palette.yellow[100] : theme.palette.background.paper,
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: '16px',
  padding: theme.spacing(2, 2, 0),
}));

export const Toolbar = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  borderTop: `1px solid ${theme.palette.divider}`,
  padding: theme.spacing(1),
}));

export const EditorContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'fullscreen',
})<FloatingWindowProps>(({ theme }) => ({
  flex: 1,
  cursor: 'text',
  display: 'flex',
  flexDirection: 'column',
  minHeight: '200px',
  overflow: 'auto',
  '.ck.ck-editor__top.ck-reset_all': {
    display: 'none',
  },
  '.ck-placeholder': {
    ...theme.typography.body2,
    margin: '0 !important',
    '&:before': {
      color: theme.palette.text.disabled,
    },
  },
  '.ck.ck-editor__editable.ck-focused:not(.ck-editor__nested-editable)': {
    boxShadow: 'none',
  },
  '.ck-content blockquote': {
    paddingLeft: '0.5em',
    borderLeft: `1px solid ${theme.palette.primary.light}`,
  },
  '.ck.ck-reset.ck-editor.ck-rounded-corners': {
    height: '100%',
    display: 'flex',
    flex: 1,
    flexDirection: 'column',

    '.ck-editor__main': {
      flexGrow: 1,
      display: 'flex',
      overflow: 'auto',

      '.ck-editor__editable_inline': {
        ...theme.typography.body2,
        fontSize: '16px',
        color: theme.palette.text.primary,
        flexGrow: 1,
        padding: 0,
        backgroundColor: 'transparent',
        border: 'none',
        overflow: 'auto',
        p: {
          margin: 0,
        },
      },
    },
  },
}));

interface FloatingWindowProps {
  fullscreen?: boolean;
  embedded?: boolean;
}
export const FloatingWindow = styled(Paper, {
  shouldForwardProp: (prop) => prop !== 'fullscreen' && prop !== 'embedded',
})<FloatingWindowProps>(({ theme, fullscreen, embedded }) => embedded ? ({
  position: 'relative',
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
  height: '100%',
  minWidth: 0,
  minHeight: 0,
  boxSizing: 'border-box',
  padding: theme.spacing(1, 2),
  borderRadius: 0,
  overflow: 'hidden',
}) : ({
  position: 'absolute',
  display: 'flex',
  flexDirection: 'column',
  top: fullscreen ? theme.spacing(5) : 'auto',
  right: fullscreen ? theme.spacing(10) : 0,
  left: fullscreen ? theme.spacing(10) : 'auto',
  bottom: fullscreen ? theme.spacing(5) : 0,
  minWidth: fullscreen ? 0 : 715,
  maxWidth: fullscreen ? '100%' : 800,
  minHeight: fullscreen ? `calc(100vh - ${theme.spacing(14)})` : 735,
  maxHeight: fullscreen ? `calc(100vh - ${theme.spacing(14)})` : `calc(100% - ${theme.spacing(30)})`,
  padding: theme.spacing(3),
  borderRadius: theme.spacing(2, 2, fullscreen ? 2 : 0, fullscreen ? 2 : 0),
  marginRight: fullscreen ? 0 : theme.spacing(8),
  zIndex: fullscreen ? theme.zIndex.drawer + 1 : theme.zIndex.fab,
  isolation: 'isolate',
  overflow: 'hidden',
  '@media(max-height: 60rem)': {
    maxHeight: 'calc(100% - 10rem)',
    minHeight: 'calc(100% - 10rem)',
  },
  [theme.breakpoints.down('sm')]: {
    position: 'fixed',
    inset: 0,
    minWidth: 0,
    maxWidth: 'none',
    minHeight: 0,
    maxHeight: 'none',
    marginRight: 0,
    padding: theme.spacing(2),
    paddingTop: `max(${theme.spacing(2)}, env(safe-area-inset-top))`,
    paddingBottom: `max(${theme.spacing(2)}, env(safe-area-inset-bottom))`,
    borderRadius: 0,
    zIndex: theme.zIndex.drawer + 1,
  },
}));
